import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { ChangePasswordModal } from "./ChangePasswordModal";
import {
  LayoutDashboard, Ticket, Users, Wrench, Package, ScrollText,
  ClipboardList, LogOut, Sun, Moon, Radar, HardHat, ShieldCheck, CalendarClock, KeyRound, UserCog, FolderOpen, FileText, PackageOpen, Wallet, Cpu, Truck, ClipboardCheck, Settings, BellRing, Banknote, UserCheck, FileSignature,
} from "lucide-react";

const NAV = [
  { to: "/app/dashboard",  label: "Dashboard",  icon: LayoutDashboard, roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/reminders",  label: "Reminders",  icon: BellRing,        roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/tickets",    label: "Service Calls", icon: Ticket,       roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/customers",  label: "Customers",  icon: Users,           roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/equipment",  label: "Equipment",  icon: Wrench,          roles: [] },
  { to: "/app/engineers",  label: "Engineers",  icon: HardHat,         roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/amc",        label: "AMC",        icon: ShieldCheck,     roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/pm",         label: "PM Schedule",icon: CalendarClock,   roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/quotations", label: "Quotations", icon: FileSignature,   roles: ["super_admin","dispatcher"] },
  { to: "/app/vendor-orders", label: "Supplier Daily Order", icon: Truck, roles: ["super_admin","dispatcher"] },
  { to: "/app/delivery-challans", label: "Delivery Challan", icon: ClipboardCheck, roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/warranty",   label: "Vendor Warranty", icon: PackageOpen, roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/spares",     label: "Spare Master", icon: Cpu,           roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/expenses",   label: "Expenses",   icon: Wallet,          roles: ["super_admin","dispatcher","engineer"] },
  { to: "/app/offer-letters", label: "Offer Letters", icon: FileText,  roles: ["super_admin","dispatcher"] },
  { to: "/app/experience-letters", label: "Experience Letters", icon: FileText, roles: ["super_admin"] },
  { to: "/app/attendance", label: "Attendance", icon: UserCheck, roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/cheques",    label: "Cheque Entry", icon: Banknote, roles: ["super_admin","dispatcher"] },
  { to: "/app/margin-calculator", label: "Margin Calculator", icon: ScrollText, roles: ["super_admin"] },
  { to: "/app/documents",  label: "Doc Drive",  icon: FolderOpen,      roles: ["super_admin","company_admin","dispatcher","engineer"] },
  { to: "/app/reports",    label: "Reports",    icon: ClipboardList,   roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/users",      label: "Users",      icon: UserCog,         roles: ["super_admin"] },
  { to: "/app/settings",   label: "Settings",   icon: Settings,        roles: ["super_admin","company_admin"] },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <aside className="hidden md:flex md:w-64 md:flex-col md:border-r md:border-border/60 md:bg-card/40 md:backdrop-blur">
      <div className="flex items-center gap-2 border-b border-border/60 px-5 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
          <Radar className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-heading text-lg font-bold leading-none tracking-tight">HCIT-CallCRM</h1>
          <p className="mt-1 label-eyebrow leading-none">Command Center</p>
        </div>
      </div>

      <nav className="flex-1 space-y-0.5 px-3 py-4">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              cn(
                "group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150",
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
              )
            }
          >
            <it.icon className="h-4 w-4" />
            <span>{it.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-border/60 p-3">
        <div className="mb-2 flex items-center gap-3 rounded-md bg-secondary/50 px-3 py-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/20 text-xs font-bold uppercase text-primary">
            {(user?.name || "?").slice(0, 1)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{user?.name}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.role?.replace(/_/g, " ")}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="theme-toggle-button"
            onClick={toggle}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-md border border-border/70 px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            {theme === "dark" ? "Light" : "Dark"}
          </button>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-md border border-border/70 px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
        </div>
      </div>
    </aside>
  );
}
